import React, { useState, useContext, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import { InView } from 'react-intersection-observer';
import 'isomorphic-unfetch';
import mergeWith from 'lodash/mergeWith';

import BackToTopIcon from 'mdi-react/ArrowUpIcon';

import Breadcrumb from '@prisjakt/ui-breadcrumb';
import Panel from '@prisjakt/ui-panel';
import { ScrollLink, ScrollElement } from '@prisjakt/ui-scroll';

import { AppState } from '../../reducers';
import modalActions from '../../components/modal/actions';

import AdContainer from '../../components/ads';
import CampaignDeals from '../../components/campaign-deals';
import ErrorBoundary from '../../components/error-boundary';
import FeedbackButton from '../feedback/feedback-button';
import Lazy from '../../components/lazy';
import Modal from '../../components/modal';
import PageContent from '../../components/page-content';
import PreReleaseInformation from './components/pre-release-information';
import PriceHistory from './components/price-history';
import PriceList from './components/price-list';
import ProductHeader from './components/product-header';
import ProductPriceHistory from '../product-price-history';
import ProductProperties from './components/product-properties';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import ReadableProductInformation from './components/readable-product-information';
import RecentlyVisitedProducts from '../../components/recently-visited';
import Recommendations from '../../components/recommendations';
import StickyTrack from '../../components/sticky-track';
import TabList from './components/tab-list';
import UserReviews from './components/user-reviews';
import { ViewportContext } from '../../components/viewport';
import { readPersonalizationSettings } from '../user/storage/personalization';
import ReviewSummary from '../../components/review-summary';

import productPageQuery from './query.graphql';

import {
  PRODUCT_ALERT_MODAL_ID,
  PRODUCT_CONTEXT_ID,
  mapOptionsToFilters,
  initialFilterState,
} from './constants';
import { getHeaderHeight, getScrollLinkOffset } from './utils';
import { useProductVariantsExperiment } from '../../hooks/experiments';

import { MarketCodes } from '../../../config/types/market';

import {
  Campaign,
  CampaignBanner,
  ProductPageQuery,
  User,
  CategoryPath,
} from '../../types';
import { Review, RouteSection, CurrentModal, CurrentCampaign } from './types';
import { TopProductIds } from './components/product-header/types';
import {
  ModelType,
  ModelTypeEnum,
} from '../../components/recommendations/types';

import { setSelectedVariant } from './actions/product';

import { trackComparisonLinkClick } from '../product-compare/tracking';

import * as S from './styles';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

const returnNull = () => null;

type OwnProps = {
  activeCampaign: Campaign;
  data: ProductPageQuery;
  fetchMoreData: () => void;
  routeSection: RouteSection;
};

type StateProps = {
  campaignBanners: {
    active: (CampaignBanner & {
      campaign: Pick<Campaign, 'media'>;
    })[];
  };
  currentModal: CurrentModal;
  me: User;
  review: Review;
};

type DispatchProps = {
  hideModal: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const ProductPage = ({
  activeCampaign,
  data,
  fetchMoreData,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  me,
  review,
  hideModal,
}: Props) => {
  const { t, linkTo, market } = useContext(AppContext);

  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const [showVariantsExperiment, setShowVariantsExperiment] = useState(false);
  const showVariants = useProductVariantsExperiment();

  const dispatch = useDispatch();

  const { product, importantDates, recommendations } = data;

  useEffect(() => {
    // Resets selected variant when entering new product page
    dispatch(setSelectedVariant({ id: 0 }));
  }, []);

  useEffect(() => {
    setShowVariantsExperiment(showVariants);
  }, [showVariants]);

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    const base = market.campaignsBaseUrl;

    const dealsEndpoint = `${base}/campaign-${market.code}-*-deals/_search`;

    fetch(dealsEndpoint, {
      method: 'POST',
      body: JSON.stringify({
        _source: ['priceInfo.priceId', 'campaign.id'],
        query: {
          bool: {
            filter: {
              terms: { 'product.id': [product.id] },
            },
          },
        },
      }),
    })
      .catch(err => {
        const fetchError = new Error(
          `FetchError: ${err.message} ${dealsEndpoint}`
        );
        throw fetchError;
      })
      .then(res => res.json())
      .then(
        res =>
          res.hits &&
          res.hits.hits &&
          setCampaignsState(
            res.hits.hits.map((hit: any) => ({
              id: hit._source.campaign.id,
              price: { id: hit._source.priceInfo.priceId.toString() },
            }))
          )
      );
  }, [product && product.id]); // eslint-disable-line

  if (!product) return null;

  product.campaigns = campaigns;

  const privacyPolicyUri =
    market &&
    market.privacySettings &&
    market.privacySettings.responsive &&
    market.privacySettings.responsive.privacyUrl;

  const currentCampaign =
    activeCampaign &&
    product.campaigns &&
    product.campaigns
      .filter(c => c && String(c.id) === String(activeCampaign.id))
      .map(match => {
        return {
          ...match,
          name: activeCampaign.name,
          path: `${
            activeCampaign.web_uri ? activeCampaign.web_uri.responsive : ''
          }${product.category ? `?category=${product.category.id}` : ''}`,
          media: { ...activeCampaign.media },
        };
      })
      .find(() => true);

  const campaignPromoBanners = {
    before:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER'),
    during:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'DURING_PROMO_BANNER'),
  };
  const campaignBannerLinkParams = product.category
    ? { category: product.category.id }
    : undefined;

  const userReviews = product.userReviews;

  const userReviewsSummary =
    review.userReviews && review.userReviews.summary
      ? review.userReviews.summary
      : product.userReviewSummary;

  const isPreRelease =
    product.releaseDate &&
    Date.parse(product.releaseDate) > Date.parse(new Date().toString());

  const similarProducts = recommendations?.find(
    item => item?.model === ModelTypeEnum.SIMILAR_PRODUCTS
  );

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const getSimilarProductsCompareLink = () => {
    if (!similarProducts?.recommendations?.length) return null;

    const products = similarProducts.recommendations
      .filter(item => item.product)
      .map(item => item.product);

    return products.length
      ? linkTo('productCompare', {
          category: product.category,
          products: product ? [product, ...products] : products,
        })
      : null;
  };

  const handleCompareLinkClick = () => {
    if (!similarProducts?.recommendations?.length) return null;

    return trackComparisonLinkClick(
      similarProducts.recommendations.length + (product ? 1 : 0),
      'similarProducts'
    );
  };

  const similarProductsCompareLink = getSimilarProductsCompareLink();

  const getRecommendationsLink = (modelType: ModelType) => {
    if (
      modelType === ModelTypeEnum.SIMILAR_PRODUCTS &&
      similarProductsCompareLink
    )
      return {
        title: t('product', 'product-compare.compare'),
        url: similarProductsCompareLink,
        onClick: handleCompareLinkClick,
      };

    if (modelType === ModelTypeEnum.PERSONALIZED)
      return {
        title: t('account', 'menu.settings'),
        url: '/personalization',
      };

    return null;
  };

  // Transform some basic information about the product into a DataList-friendly shape to pass into ProductProperties
  const basicInfo = () => {
    if (!product) {
      return [];
    }

    const { brand, category } = product;
    const brandProperties = brand
      ? [
          {
            name: t('product', 'property.brand'),
            value: {
              type: 'string',
              text: brand.name,
              link: brand.pathName,
            },
          },
        ]
      : [];
    const categoryProperties = category
      ? [
          {
            name: t('product', 'property.category'),
            value: {
              type: 'string',
              text: category.name,
              link: category.pathName,
            },
          },
        ]
      : [];

    return [
      ...[
        {
          name: t('product', 'property.product-name'),
          value: { type: 'string', text: product.name },
        },
      ],
      ...brandProperties,
      ...categoryProperties,
    ];
  };

  const onHeaderVisibilityChange = (isVisible: boolean) => {
    if (isHeaderVisible !== isVisible) {
      setHeaderVisible(isVisible);
    }
  };

  const getCategoryTopProducts = () => {
    if (
      !product.category ||
      !product.category.products ||
      !product.category.products.nodes ||
      !product.category.products.nodes.length
    )
      return null;

    // Returns array with current product id + top 10 product ids in same category.
    return [
      { id: product.id },
      ...product.category.products.nodes.filter(
        node => node && node.id !== product.id
      ),
    ] as TopProductIds;
  };

  // We want to test rendering all tabbed sections on load in DK
  // by measuring the SEO vs performance impact.
  // All tabs and rendered in the DOM but non active are hidden with CSS.
  const shouldRenderAllTabs = market.code === 'dk';

  return (
    <>
      {me && me.analyticsAccess && (
        <FeedbackButton position="right">
          {t('feedback', 'button.text')}
        </FeedbackButton>
      )}

      <PageContent withAds>
        <S.ProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={hideModal}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={hideModal}
                privacyPolicyUri={privacyPolicyUri || undefined}
              />
            </Modal>
          )}
          {campaignPromoBanners.during &&
            campaignPromoBanners.during.length > 0 && (
              <div className="column small-12">
                <PromoBanner
                  banners={campaignPromoBanners.during}
                  linkParams={campaignBannerLinkParams}
                />
              </div>
            )}

          <S.BreadCrumbWrapper>
            <Breadcrumb
              homeText={t('common', 'breadcrumb.home')}
              path={
                product.category?.path
                  ? (product.category.path.filter(x => x) as CategoryPath[])
                  : []
              }
              firstPathSelectable
            />
          </S.BreadCrumbWrapper>
          <ScrollElement name="Top">
            <ViewportContext.Consumer>
              {({ isMobile }) => (
                <>
                  <InView
                    onChange={onHeaderVisibilityChange}
                    rootMargin={`-${getHeaderHeight(isMobile) - 10}px`}
                  >
                    <ProductHeader
                      title={product.name}
                      product={product}
                      currentCampaign={currentCampaign}
                      categoryTopProducts={getCategoryTopProducts()}
                    />
                  </InView>

                  <ScrollLink to="Top" onClick={blurActiveElement}>
                    <S.BackToTopButton
                      aria-label={t(
                        'product',
                        'back-to-top.button-description'
                      )}
                    >
                      <BackToTopIcon />
                    </S.BackToTopButton>
                  </ScrollLink>

                  <ScrollElement name="Content">
                    <StickyTrack
                      topMargin={0}
                      headerHeight={getHeaderHeight(isMobile)}
                      renderElement={
                        <TabList
                          routeSection={routeSection}
                          product={product}
                          isSticky={!isHeaderVisible}
                        />
                      }
                    >
                      <S.TabContentWrapper spacingTop={routeSection !== 'main'}>
                        {!shouldRenderAllTabs ? (
                          <>
                            <S.TabContent id="main">
                              {routeSection === 'main' && (
                                <>
                                  {isPreRelease && product.releaseDate && (
                                    <PreReleaseInformation
                                      product={product}
                                      releaseDate={product.releaseDate}
                                    />
                                  )}
                                  {product.statistics &&
                                    product.statistics.pageInfo &&
                                    product.statistics.pageInfo.lowestPrice &&
                                    isPreRelease && (
                                      <PriceHistory
                                        route={linkTo(
                                          'productPriceHistory',
                                          product
                                        )}
                                        productName={product.name}
                                        {...product.statistics}
                                        predictedPrices={
                                          product.priceForecast
                                            ? product.priceForecast
                                            : undefined
                                        }
                                      />
                                    )}
                                  {isPreRelease &&
                                  similarProducts?.recommendations?.length ? (
                                    <Recommendations
                                      link={getRecommendationsLink(
                                        ModelTypeEnum.SIMILAR_PRODUCTS
                                      )}
                                      recommendations={
                                        similarProducts.recommendations
                                      }
                                      type={ModelTypeEnum.SIMILAR_PRODUCTS}
                                      contextId={PRODUCT_CONTEXT_ID}
                                    />
                                  ) : null}
                                  {product.prices && (
                                    <ScrollElement
                                      name="Pricelist"
                                      data-test="MainTabContent"
                                    >
                                      <PriceList
                                        {...product}
                                        media={product.media}
                                        productName={product.name}
                                        adOffset={1}
                                        campaign={
                                          currentCampaign as CurrentCampaign
                                        }
                                        showVariantsExperiment={
                                          showVariantsExperiment
                                        }
                                        variantGroups={product.variantGroups}
                                      />
                                    </ScrollElement>
                                  )}

                                  <ReadableProductInformation
                                    product={product}
                                  />
                                </>
                              )}
                            </S.TabContent>

                            <S.TabContent id="properties">
                              {routeSection === 'properties' && (
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Properties"
                                    data-test="PropertiesTabContent"
                                  >
                                    <ProductProperties
                                      basicInfo={basicInfo()}
                                      properties={
                                        product.properties || undefined
                                      }
                                      description={
                                        product.description || undefined
                                      }
                                      getScrollLinkOffset={getScrollLinkOffset}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>

                            <S.TabContent id="reviews">
                              {routeSection === 'reviews' && (
                                <ErrorBoundary onError={returnNull}>
                                  <>
                                    <AdContainer
                                      placement="panorama_extra"
                                      format="panorama_1_wde"
                                      min={861}
                                      max={10000}
                                      scalable
                                      scaleBase=".pj-ui-panel--row"
                                      spacing={{
                                        top: '10px',
                                        bottom: '10px',
                                      }}
                                    />

                                    <AdContainer
                                      placement="dubbelpanorama1"
                                      format="panorama_1"
                                      min={0}
                                      max={860}
                                      spacing={{
                                        top: '10px',
                                        bottom: '10px',
                                      }}
                                    />

                                    <S.ReviewsWrapper data-test="ReviewsTabContent">
                                      <S.Reviews>
                                        <UserReviews
                                          belongsTo={{
                                            id: product.id,
                                            name: product.name,
                                            type: 'product',
                                          }}
                                          myReview={
                                            userReviews && userReviews.myReview
                                          }
                                          nodes={
                                            userReviews && userReviews.nodes
                                          }
                                          pageInfo={
                                            userReviews && userReviews.pageInfo
                                          }
                                          showAds={false}
                                        />
                                      </S.Reviews>

                                      <S.ReviewsSidebar>
                                        <ReviewSummary
                                          rating={userReviewsSummary.rating}
                                          count={userReviewsSummary.count}
                                          countTotal={
                                            userReviewsSummary.countTotal
                                          }
                                          distribution={
                                            userReviewsSummary.distribution
                                          }
                                          myReview={userReviews?.myReview}
                                          belongsTo={{
                                            id: product.id,
                                            name: product.name,
                                            type: 'product',
                                          }}
                                          showPrompt
                                        />
                                      </S.ReviewsSidebar>
                                    </S.ReviewsWrapper>
                                  </>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>

                            <S.TabContent id="statistics">
                              {routeSection === 'statistics' && (
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Information"
                                    data-test="StatisticsTabContent"
                                  >
                                    <ProductPriceHistory
                                      data={{ product, importantDates }}
                                      fetchMoreData={fetchMoreData}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>
                          </>
                        ) : (
                          <>
                            <S.TabContent id="main">
                              <S.HideableContent
                                visible={routeSection === 'main'}
                              >
                                {isPreRelease && product.releaseDate && (
                                  <PreReleaseInformation
                                    product={product}
                                    releaseDate={product.releaseDate}
                                  />
                                )}
                                {product.statistics &&
                                  product.statistics.pageInfo &&
                                  product.statistics.pageInfo.lowestPrice &&
                                  isPreRelease && (
                                    <PriceHistory
                                      route={linkTo(
                                        'productPriceHistory',
                                        product
                                      )}
                                      productName={product.name}
                                      {...product.statistics}
                                      predictedPrices={
                                        product.priceForecast
                                          ? product.priceForecast
                                          : undefined
                                      }
                                    />
                                  )}
                                {isPreRelease &&
                                similarProducts?.recommendations?.length ? (
                                  <Recommendations
                                    link={getRecommendationsLink(
                                      ModelTypeEnum.SIMILAR_PRODUCTS
                                    )}
                                    recommendations={
                                      similarProducts.recommendations
                                    }
                                    type={ModelTypeEnum.SIMILAR_PRODUCTS}
                                    contextId={PRODUCT_CONTEXT_ID}
                                  />
                                ) : null}
                                {product.prices && (
                                  <ScrollElement
                                    name="Pricelist"
                                    data-test="MainTabContent"
                                  >
                                    <PriceList
                                      {...product}
                                      adOffset={1}
                                      campaign={
                                        currentCampaign as CurrentCampaign
                                      }
                                      showVariantsExperiment={
                                        showVariantsExperiment
                                      }
                                      variantGroups={product.variantGroups}
                                    />
                                  </ScrollElement>
                                )}
                                <ReadableProductInformation product={product} />
                              </S.HideableContent>
                            </S.TabContent>
                            <S.TabContent id="properties">
                              <S.HideableContent
                                visible={routeSection === 'properties'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Properties"
                                    data-test="PropertiesTabContent"
                                  >
                                    <ProductProperties
                                      basicInfo={basicInfo()}
                                      properties={
                                        product.properties || undefined
                                      }
                                      description={
                                        product.description || undefined
                                      }
                                      getScrollLinkOffset={getScrollLinkOffset}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>

                            <S.TabContent id="reviews">
                              <S.HideableContent
                                visible={routeSection === 'reviews'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Reviews"
                                    data-test="ReviewsTabContent"
                                  >
                                    <AdContainer
                                      placement="panorama_extra"
                                      format="panorama_1_wde"
                                      min={861}
                                      max={10000}
                                      scalable
                                      scaleBase=".pj-ui-panel--row"
                                      spacing={{
                                        top: '10px',
                                        bottom: '10px',
                                      }}
                                    />

                                    <AdContainer
                                      placement="dubbelpanorama1"
                                      format="panorama_1"
                                      min={0}
                                      max={860}
                                      spacing={{
                                        top: '10px',
                                        bottom: '10px',
                                      }}
                                    />

                                    <S.ReviewsWrapper>
                                      <S.Reviews>
                                        <UserReviews
                                          belongsTo={{
                                            id: product.id,
                                            name: product.name,
                                            type: 'product',
                                          }}
                                          myReview={
                                            userReviews && userReviews.myReview
                                          }
                                          nodes={
                                            userReviews && userReviews.nodes
                                          }
                                          pageInfo={
                                            userReviews && userReviews.pageInfo
                                          }
                                          showAds={false}
                                        />
                                      </S.Reviews>

                                      <S.ReviewsSidebar>
                                        <ReviewSummary
                                          rating={userReviewsSummary.rating}
                                          count={userReviewsSummary.count}
                                          countTotal={
                                            userReviewsSummary.countTotal
                                          }
                                          distribution={
                                            userReviewsSummary.distribution
                                          }
                                          myReview={userReviews?.myReview}
                                          belongsTo={{
                                            id: product.id,
                                            name: product.name,
                                            type: 'product',
                                          }}
                                          showPrompt
                                        />
                                      </S.ReviewsSidebar>
                                    </S.ReviewsWrapper>
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>
                            <S.TabContent id="statistics">
                              <S.HideableContent
                                visible={routeSection === 'statistics'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Information"
                                    data-test="StatisticsTabContent"
                                  >
                                    <ProductPriceHistory
                                      data={{ product, importantDates }}
                                      fetchMoreData={fetchMoreData}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>
                          </>
                        )}
                        <Panel>
                          <AdContainer
                            placement="panorama_target"
                            format="panorama_3_wde"
                            min={861}
                            max={10000}
                            scalable
                            scaleBase=".pj-ui-panel--row"
                            triggerPoint={100}
                          />
                        </Panel>
                        {product.category &&
                          product.category.deals &&
                          product.category.deals.length > 0 && (
                            <ErrorBoundary onError={returnNull}>
                              <Lazy offset={100}>
                                <CampaignDeals
                                  deals={product.category.deals}
                                  campaignLinkQuery={`?category=${product.category.id}`}
                                />
                              </Lazy>
                            </ErrorBoundary>
                          )}
                        <section>
                          <Lazy offset={100}>
                            {recommendations?.map(item => {
                              if (!item.recommendations?.length) return null;
                              return (
                                <Recommendations
                                  key={`recommendations-model-${item.model}`}
                                  link={getRecommendationsLink(
                                    item.model as ModelType
                                  )}
                                  recommendations={item.recommendations}
                                  type={item.model as ModelType}
                                  contextId={PRODUCT_CONTEXT_ID}
                                />
                              );
                            })}
                          </Lazy>

                          <Lazy offset={100}>
                            <RecentlyVisitedProducts offset={1} />
                          </Lazy>
                        </section>
                        {campaignPromoBanners.before &&
                          campaignPromoBanners.before.length > 0 && (
                            <PromoBanner
                              inverted
                              banners={campaignPromoBanners.before}
                            />
                          )}
                      </S.TabContentWrapper>
                    </StickyTrack>
                  </ScrollElement>
                </>
              )}
            </ViewportContext.Consumer>
          </ScrollElement>
        </S.ProductPage>
      </PageContent>
    </>
  );
};

ProductPage.graphQL = productPageQuery;

type Group = {
  match: Match;
  name: string;
};

type Match = {
  isExact: boolean;
  params: { id: number; section: string };
  path: string;
  url: string;
};

type Variables = {
  id: string;
  offset: string;
  section: RouteSection;
  marketCode: MarketCodes;
  experiments: {
    [key in keyof string]: {
      name: string;
      optimizeId: string;
      selectedVariant: string;
      shouldCache: boolean;
      variantIndex: number;
    };
  };
};

type Loaded = {
  badges: boolean;
  campaign: boolean;
  campaignDeals: boolean;
  main: boolean;
  media: boolean;
  priceHistory: boolean;
  priceList: boolean;
  productProperties: boolean;
  relatedProducts: boolean;
  userActions: boolean;
  userReviews: boolean;
  deliveryOptions: boolean;
};

type RouteReducerState = {
  options: {
    showMorePrices: boolean;
    showInternational: boolean;
    showUsedProducts: boolean;
    showNotInMintCondition: boolean;
    showUnavailable: boolean;
    showPickupOnly: boolean;
  };
  showVariantsExperiment: boolean;
  variants: number[] | null;
};

type RouteReducerActions = {
  type: 'PRICE_FILTER' | 'PRODUCT_VARIANTS';
  options: RouteReducerState['options'];
  variants: RouteReducerState['variants'];
  showVariantsExperiment: RouteReducerState['showVariantsExperiment'];
};

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({
  id,
  offset,
  section,
  marketCode,
  experiments,
}: Variables) => {
  const excludeTypes = Object.keys(initialFilterState)
    .filter(option => !initialFilterState[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
    personalizationExcludeCategories: readPersonalizationSettings().notInterestedIn.categories.map(
      (category: { id: string }) => +category.id
    ),
    recommendationsContextId: PRODUCT_CONTEXT_ID,
    includeSecondary:
      !experiments['product-variants'] ||
      experiments['product-variants'].selectedVariant === 'baseline',
    excludeTypes:
      !experiments['product-variants'] ||
      experiments['product-variants'].selectedVariant === 'baseline'
        ? []
        : excludeTypes,
    variants: null,
  };
};

ProductPage.reducer = (
  state: RouteReducerState,
  action: RouteReducerActions
) => {
  switch (action.type) {
    case 'PRICE_FILTER':
      return {
        ...state,
        options: action.options,
        variants: action.variants,
        showVariantsExperiment: action.showVariantsExperiment,
      };

    case 'PRODUCT_VARIANTS':
      return {
        ...state,
        variants: action.variants,
        options: action.options,
        showVariantsExperiment: action.showVariantsExperiment,
      };

    default:
      return state;
  }
};

ProductPage.stateToVariables = (state: RouteReducerState) => {
  if (!state || !state.options || !state.showVariantsExperiment) return {};

  const excludeTypes = Object.keys(state.options)
    .filter(option => !state.options[option])
    .map(filter => mapOptionsToFilters[filter])
    .filter(x => x);

  return {
    ...state,
    excludeTypes,
    includeSecondary: state.options?.showInternational,
    priceList: true,
    variants: state.variants,
  };
};

ProductPage.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  const firstLoad =
    // We need to fetch all sections data for the experiment being run in DK
    marketCode === 'dk'
      ? {
          ...sectionContent.main,
          ...sectionContent.properties,
          ...sectionContent.reviews,
          ...sectionContent.statistics,
        }
      : {
          ...sectionContent[section],
        };

  return [
    {
      main: !loaded.main,
      ...firstLoad,
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

ProductPage.mergeData = (
  oldData: ProductPageQuery,
  newData: ProductPageQuery
) => {
  const data = mergeWith({}, oldData, newData);

  // If price data was refetched always use data from latest fetch
  if (newData?.product?.prices) {
    return {
      ...data,
      product: {
        ...data.product,
        prices: newData.product.prices,
        variantGroups: newData.product.variantGroups,
      },
    };
  }

  return data;
};

const mapStateToProps = (state: AppState) => ({
  campaignBanners: state.globals.campaignBanners,
  review: state.areas.review,
  currentModal: state.modal.current,
  me: state.areas.account.me,
});

const dispatchProps = {
  hideModal: modalActions.hideModal,
};

export default connect(mapStateToProps, dispatchProps)(ProductPage);
